import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { bodyMetrics as seedBody, defaultHiddenBodyMetrics, defaultPublicMetrics } from '../data/bodyMetrics';
import { people } from '../data/people';
import type { BodyMetric, BodyMetricId, UnitSystem } from '../types';
import { calculateBmi } from '../utils/units';
interface ProfileDraft {
  name: string;
  username: string;
  bio: string;
}
interface MetricsValue {
  unitSystem: UnitSystem;
  setUnitSystem: (system: UnitSystem) => void;
  /** Editable body metrics plus the derived BMI, in display order. */
  bodyMetrics: BodyMetric[];
  bmi: number;
  currentWeightKg: number | null;
  updateBodyMetric: (id: BodyMetricId, value: number) => void;
  hiddenMetrics: string[];
  isHidden: (id: string) => boolean;
  toggleHidden: (id: string) => void;
  restoreHidden: () => void;
  publicMetrics: string[];
  isPublic: (id: string) => boolean;
  togglePublic: (id: string) => void;
  profile: ProfileDraft;
  updateProfile: (draft: ProfileDraft) => void;
}
const MetricsContext = createContext<MetricsValue | null>(null);
export function MetricsProvider({
  weightAvailable = true,
  children
}: {
  /** Prototype: open with no recorded weight to review the dependency state. */
  weightAvailable?: boolean;
  children: React.ReactNode;
}) {
  const me = people[0];
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
  const [values, setValues] = useState<BodyMetric[]>(() => weightAvailable ? seedBody : seedBody.map(metric => metric.id === 'weight' ? {
    ...metric,
    value: 0
  } : metric));
  const [hiddenMetrics, setHidden] = useState<string[]>(defaultHiddenBodyMetrics);
  const [publicMetrics, setPublic] = useState<string[]>(defaultPublicMetrics);
  const [profile, setProfile] = useState<ProfileDraft>({
    name: me.name,
    username: me.username,
    bio: me.bio['pt-BR']
  });
  const weight = values.find(metric => metric.id === 'weight')?.value ?? 0;
  const height = values.find(metric => metric.id === 'height')?.value ?? 0;
  // BMI is derived only when BOTH weight and height exist; otherwise it is shown as "—".
  const bmi = weight > 0 && height > 0 ? calculateBmi(weight, height) : 0;
  /** Latest weight in kg (stored canonically in kg), or null when missing. */
  const currentWeightKg = weight > 0 ? weight : null;
  const updateBodyMetric = useCallback((id: BodyMetricId, value: number) => {
    setValues(prev => prev.map(metric => metric.id === id ? {
      ...metric,
      value
    } : metric));
  }, []);
  const toggleId = useCallback((setter: React.Dispatch<React.SetStateAction<string[]>>, id: string) => setter(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]), []);
  const value = useMemo<MetricsValue>(() => ({
    unitSystem,
    setUnitSystem,
    bodyMetrics: [...values, {
      id: 'bmi',
      kind: 'bmi',
      value: bmi,
      editable: false
    }],
    bmi,
    currentWeightKg,
    updateBodyMetric,
    hiddenMetrics,
    isHidden: (id: string) => hiddenMetrics.includes(id),
    toggleHidden: (id: string) => toggleId(setHidden, id),
    restoreHidden: () => setHidden([]),
    publicMetrics,
    isPublic: (id: string) => publicMetrics.includes(id),
    togglePublic: (id: string) => toggleId(setPublic, id),
    profile,
    updateProfile: setProfile
  }), [unitSystem, values, bmi, currentWeightKg, updateBodyMetric, hiddenMetrics, publicMetrics, profile, toggleId]);
  return <MetricsContext.Provider value={value} data-id="element-1520" data-mp="12dqu7y87c113">{children}</MetricsContext.Provider>;
}
export function useMetrics(): MetricsValue {
  const ctx = useContext(MetricsContext);
  if (!ctx) throw new Error('useMetrics must be used inside MetricsProvider');
  return ctx;
}